import React, { useState, useEffect, useRef } from 'react';
import { Upload, Plus, X, Image as ImageIcon, ArrowUp, ClipboardCopy } from 'lucide-react';

const UploadImageDialog = ({ isOpen, onClose, projectId, onImagesUploaded }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);
  const userId = JSON.parse(localStorage.getItem('user'))?.userId;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setError(null);
      setUploadProgress(0);
    }
  }, [isOpen]);

  // Handle clipboard paste
  useEffect(() => {
    const handlePaste = (e) => {
      if (!isOpen) return;
      
      if (e.clipboardData && e.clipboardData.items) {
        const items = Array.from(e.clipboardData.items);
        const imageItems = items.filter(item => item.type.indexOf('image') !== -1);
        
        if (imageItems.length > 0) {
          e.preventDefault();
          const files = imageItems.map(item => {
            const file = item.getAsFile();
            // Give pasted images a more descriptive name
            const newFile = new File(
              [file], 
              `Pasted_Image_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${file.name.split('.').pop() || 'png'}`,
              { type: file.type }
            );
            return newFile;
          });
          
          setSelectedFiles(prev => [...prev, ...files]);
        }
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const validFiles = Array.from(e.target.files).filter(
        file => file.type.match(/^image\/(jpeg|png|webp)$/)
      );
      
      if (validFiles.length !== e.target.files.length) {
        setError('Only JPEG, PNG, and WebP images are supported. Some files were skipped.');
      }
      
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const validFiles = Array.from(e.dataTransfer.files).filter(
        file => file.type.match(/^image\/(jpeg|png|webp)$/)
      );
      
      if (validFiles.length !== e.dataTransfer.files.length) {
        setError('Only JPEG, PNG, and WebP images are supported. Some files were skipped.');
      }
      
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      } else if (e.dataTransfer.files.length > 0) {
        setError('Please drop only image files (JPEG, PNG, WebP)');
      }
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !projectId || !userId) {
      setError(!selectedFiles.length ? 'No files selected' : 'Missing project or user information');
      return;
    }
    
    try {
      setIsUploading(true);
      setError(null);
      
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('file', file);
      });
      
      // CRITICAL FIX: Explicitly set projectType to snippet in formData
      formData.append('projectType', 'snippet');
      formData.append('projectName', 'Update'); // Just a placeholder, the backend uses the existing project name
      formData.append('userId', userId);
      formData.append('projectId', projectId); // This tells the backend it's an existing project
      
      // Track upload progress
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progressPercent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progressPercent);
        }
      };
      
      // Add timeout to detect stalled uploads
      let uploadTimerId;
      const resetUploadTimer = () => {
        clearTimeout(uploadTimerId);
        uploadTimerId = setTimeout(() => {
          setError('Upload seems to be taking longer than expected. Please wait or try again later.');
        }, 30000); // 30 seconds timeout
      };
      
      resetUploadTimer(); // Start initial timer
      
      // Handle response
      xhr.onload = function() {
        clearTimeout(uploadTimerId);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            onImagesUploaded(response);
            onClose();
          } catch (err) {
            setError('Invalid response from server');
            setIsUploading(false);
          }
        } else {
          let errorMessage = 'Upload failed';
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            errorMessage = errorResponse.error || errorMessage;
          } catch (e) {
            console.error('Could not parse error response', e);
          }
          setError(errorMessage);
          setIsUploading(false);
        }
      };
      
      xhr.onerror = function() {
        clearTimeout(uploadTimerId);
        setError('Network error occurred during upload');
        setIsUploading(false);
      };
      
      // CRITICAL FIX: Explicitly include projectType=snippet in the URL query parameter
      xhr.open('POST', `${process.env.REACT_APP_API_URL}/api/upload?projectType=snippet`);
      xhr.send(formData);
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload images');
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div 
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-dialog-title"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 
              id="upload-dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-white"
            >
              Add Images to Project
            </h2>
            <button
              onClick={onClose}
              disabled={isUploading}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 disabled:opacity-50"
              aria-label="Close dialog"
            >
              <X size={20} />
            </button>
          </div>
          
          <div
            className={`border-2 ${
              isDragging 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-dashed border-gray-300 dark:border-gray-700'
            } rounded-lg p-6 flex flex-col items-center justify-center mb-4 transition-colors
            ${isUploading ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileChange}
              accept="image/jpeg,image/png,image/webp"
              multiple
              disabled={isUploading}
            />
            
            {selectedFiles.length > 0 ? (
              <div className="w-full">
                <h3 className="font-medium mb-2 text-gray-900 dark:text-white">
                  Selected files ({selectedFiles.length})
                </h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 mb-3 max-h-[180px] overflow-auto">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="flex items-center gap-2 mb-1.5 py-1 px-2 rounded group hover:bg-gray-50 dark:hover:bg-gray-700">
                      <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center flex-shrink-0">
                        <ImageIcon size={16} className="text-gray-500 dark:text-gray-400" />
                      </div>
                      <span className="truncate flex-1">{file.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      {!isUploading && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1"
                          aria-label="Remove file"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {!isUploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFiles([]);
                    }}
                    className="text-sm text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Clear selection
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-full mb-3">
                  <Plus size={24} className="text-gray-500 dark:text-gray-400" />
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-2 text-center">
                  Drag images here or click to browse
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Supports: JPG, PNG, WebP
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="h-px bg-gray-200 dark:bg-gray-700 w-16"></div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">OR</span>
                  <div className="h-px bg-gray-200 dark:bg-gray-700 w-16"></div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <ClipboardCopy size={16} />
                  <span>Press <kbd className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">Ctrl+V</kbd> to paste from clipboard</span>
                </div>
              </>
            )}
          </div>
          
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          {isUploading && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Uploading images...
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {uploadProgress}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Please wait while your images are being uploaded and processed
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg
                transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <ArrowUp size={16} />
                  <span>Upload Images</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadImageDialog;